import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { map, mapTo, shareReplay, take } from 'rxjs/operators';

import {
    CustomHttpParamEncoder,
    mapToPaginatedResult,
    OrganisationId,
    PaginatedResults,
    PaginatedUser,
    PaginationInfo,
    QueryData,
    Role,
    RoleId,
    SearchState,
    ShAbstractHttpService,
    ShApiCachingHelper,
    ShPaginationService,
    SortInfo,
    User,
    UserId
} from '@stylehaven/components';

@Injectable({ providedIn: 'root' })
export class RolesService {
    private readonly defaultHeaders: HttpHeaders = new HttpHeaders();
    private readonly rolesEndpoint: string = 'v2/roles';
    private roles$: Observable<Role[]>;

    // Temporary until filtering on type is possible
    private ROLES_BLACKLIST: string[] = ['SYSTEM', 'SYSTEM_ALL_ORGANISATIONS', 'BULK_UPLOAD'];

    constructor(
        protected http: ShAbstractHttpService,
        private apiCachingHelper: ShApiCachingHelper,
        private readonly paginationService: ShPaginationService
    ) {}

    // #region init

    private initRoles(): Observable<Role[]> {
        const cachedRoles$: Observable<any> = this.apiCachingHelper.cachedApiCall(this.getAllRoles());

        cachedRoles$.subscribe(updatedRoles => (this.roles$ = of(updatedRoles)));

        return cachedRoles$.pipe(take(1));
    }

    // #endregion

    // #region GET

    private getAllRoles(): Observable<Role[]> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set(
            'onlyAssignable',
            true
        );
        return this.http
            .get<Role[]>(this.rolesEndpoint, params)
            .pipe(map(results => this.filterBlacklistedRoles(results)));
    }

    public getRoles(): Observable<Role[]> {
        return this.roles$ ? this.roles$ : this.initRoles();
    }

    public getRolesByOrgId(orgId: OrganisationId): Observable<Role[]> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set(
            'organisationId',
            orgId
        );
        return this.http
            .get<Role[]>(this.rolesEndpoint, params)
            .pipe(map(results => this.filterBlacklistedRoles(results)));
    }

    public getRoleList(searchState: SearchState, organisationId: OrganisationId): Observable<PaginatedResults<Role>> {
        const sortInfo: SortInfo = searchState.sortInfo;
        const paginationInfo: PaginationInfo = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize
        };
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;

        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('organisationId', organisationId)
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);

        searchState.structuredQuery.Fields?.forEach(queryPart => {
            if (queryPart.values.length > 0) {
                let wildcardSearch = false;
                const field: string = queryPart.field.replace('*', () => {
                    wildcardSearch = true;
                    return '';
                });

                let value: string = queryPart.values.join('');
                if (wildcardSearch) {
                    value = `*${value}*`;
                }

                params = params.set(field, value);
            }
        });

        return this.http.get<Role[]>(this.rolesEndpoint, params).pipe(
            map(results => this.filterBlacklistedRoles(results)),
            map(results =>
                mapToPaginatedResult(
                    {
                        TotalNrOfResults: results.length,
                        StartIndex: startIndex,
                        NrOfResults: results.length,
                        Results: results.slice(startIndex, startIndex + paginationInfo.resultsPerPage)
                    },
                    paginationInfo.resultsPerPage
                )
            )
        );
    }

    public getRoleById(id: RoleId): Observable<Role> {
        const request = `${this.rolesEndpoint}/${id}`;
        return this.http.get<Role>(request);
    }

    public getPaginatedRoleUsers(roleId: RoleId, queryData: QueryData): Observable<PaginatedResults<PaginatedUser>> {
        const paginationInfo: PaginationInfo = queryData.paginationInfo;
        const filterValue: string = (queryData.structuredQuery.Fields ?? [])
            .map(filter => filter.values.map(value => `*${value}*`))
            .join('');
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('name', filterValue);
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;
        const url = `${this.rolesEndpoint}/${roleId}/users`;

        return this.paginationService
            .get<PaginatedUser>(url, params, this.defaultHeaders, startIndex, paginationInfo.resultsPerPage)
            .pipe(
                map(results => ({
                    ...results,
                    Results: results.Results.map(user => new PaginatedUser(user))
                }))
            )
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getAllRoleUsers(roleId: RoleId): Observable<User[]> {
        const url = `${this.rolesEndpoint}/${roleId}/users`;
        return this.apiCachingHelper.cachedApiCall(this.paginationService.getAll<User>(url));
    }

    // #endregion

    // #region POST

    public createRole(roleData: Role): Observable<Role> {
        return this.http.post<Role>(this.rolesEndpoint, roleData).pipe(shareReplay(1));
    }

    public addUsersToRole(roleId: RoleId, userIds: UserId[]): Observable<boolean[]> {
        if (userIds.length === 0) {
            return of([true]);
        }

        const requestStreams: Observable<boolean>[] = [];
        const request = `${this.rolesEndpoint}/${roleId}/users`;
        // limited to 20 users per request
        for (let start = 0; start < userIds.length; start += 20) {
            requestStreams.push(this.http.post<void>(request, userIds.splice(start, start + 20)).pipe(mapTo(true)));
        }
        return combineLatest(requestStreams);
    }

    // #endregion

    // #region PUT

    public updateRole(roleData: Role): Observable<Role> {
        const request = `${this.rolesEndpoint}/${roleData.RoleId}`;
        return this.http.put<Role>(request, roleData).pipe(shareReplay(1));
    }

    // #endregion

    // #region DELETE

    public deleteRole(roleId: RoleId): Observable<boolean> {
        const request = `${this.rolesEndpoint}/${roleId}`;
        return this.http.delete<boolean>(request).pipe(mapTo(true));
    }

    public removeUsersFromRole(roleId: RoleId, userIds: UserId[]): Observable<boolean[]> {
        if (userIds.length === 0) {
            return of([true]);
        }

        const requestStreams: Observable<boolean>[] = [];
        const request = `${this.rolesEndpoint}/${roleId}/users`;

        // limited to 20 users per request
        for (let start = 0; start < userIds.length; start += 20) {
            //MED-2355: No body send with delete request
            const userSubset: UserId[] = userIds.splice(start, start + 20);
            requestStreams.push(this.http.delete<void>(request, { body: userSubset }).pipe(mapTo(true)));
        }

        return combineLatest(requestStreams);
    }

    // #endregion

    // #region HEAD
    public getUserCountByRoleId(id: RoleId): Observable<number> {
        return this.http.count(`${this.rolesEndpoint}/${id}/users`);
    }
    // #endregion

    // #region util

    private filterBlacklistedRoles(roles: Role[]): Role[] {
        return roles.filter(role => !this.ROLES_BLACKLIST.includes(role.Label));
    }

    // #endregion
}
